import React, { useContext } from 'react';
import { Text } from '@fluentui/react';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../util/localStorageUtils';

export type BadgeVariant =
    | 'primary'
    | 'neutral'
    | 'success'
    | 'warning'
    | 'error'
    | 'info';

interface IStatusBadge {
    text: string;
    variant?: BadgeVariant;
    size?: 'small' | 'medium';
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: () => void;
    style?: React.CSSProperties;
}

export const StatusBadge: React.FC<IStatusBadge> = ({
    text,
    variant = 'neutral',
    size = 'medium',
    onMouseEnter,
    onMouseLeave,
    onClick,
    style = {},
}) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const getBaseStyle = () => {
        const horizontalPadding = size === 'small' ? 6 : 8;
        const verticalPadding = size === 'small' ? 2 : 4;

        return {
            padding: `${verticalPadding}px ${horizontalPadding}px`,
            borderRadius: size === 'small' ? 2 : 4,
            fontSize:
                size === 'small'
                    ? theme.fonts.small.fontSize
                    : theme.fonts.medium.fontSize,
            fontWeight: 500,
            cursor: onClick ? 'pointer' : 'default',
            lineHeight:
                size === 'small'
                    ? theme.fonts.small.lineHeight
                    : theme.fonts.medium.lineHeight,
            display: 'inline-flex',
            alignItems: 'center',
            userSelect: 'none' as const,
            transition: 'background-color 0.2s ease',
        };
    };

    const getVariantStyle = () => {
        const baseStyle = getBaseStyle();

        switch (variant) {
            case 'primary':
                return {
                    ...baseStyle,
                    color: theme.palette.white,
                    backgroundColor: theme.palette.themePrimary,
                    border: `1px solid ${theme.palette.themePrimary}`,
                };

            case 'success':
                return {
                    ...baseStyle,
                    color: theme.palette.white,
                    backgroundColor:
                        theme.semanticColors.successBackground ||
                        theme.palette.green,
                    border: `1px solid ${
                        theme.semanticColors.successIcon ||
                        theme.palette.greenDark
                    }`,
                };

            case 'warning':
                return {
                    ...baseStyle,
                    color: theme.palette.neutralDark,
                    backgroundColor: theme.semanticColors.warningBackground,
                    border: `1px solid ${theme.semanticColors.warningHighlight}`,
                };

            case 'error':
                return {
                    ...baseStyle,
                    color: theme.palette.white,
                    backgroundColor: theme.semanticColors.errorBackground,
                    border: `1px solid ${theme.semanticColors.errorIcon}`,
                };

            case 'info':
                return {
                    ...baseStyle,
                    color: theme.palette.white,
                    backgroundColor: theme.palette.themeDark,
                    border: `1px solid ${theme.palette.themeDarkAlt}`,
                };

            case 'neutral':
            default:
                return {
                    ...baseStyle,
                    color: theme.palette.neutralDark,
                    backgroundColor: theme.palette.neutralLighter,
                    border: `1px solid ${theme.palette.neutralLight}`,
                };
        }
    };

    const finalStyle = {
        ...getVariantStyle(),
        ...style,
    };

    return (
        <Text
            as="span"
            variant={size === 'small' ? 'small' : 'medium'}
            style={finalStyle}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
        >
            {text}
        </Text>
    );
};
